import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FaqsService, PublicFaqItem, PublicFaqGroup } from '../../core/services/faqs.service';
import { LucideIconsModule } from '../../shared/lucide-icons.module';

export interface FaqCategoryGroup {
  category: string;
  slug: string;
  icon: string;
  color: string;
  bgColor: string;
  items: PublicFaqItem[];
  displayCount: number;
  expanded: boolean;
  shareCopied?: boolean;
}

@Component({
  selector: 'app-faq-page',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideIconsModule],
  templateUrl: './faq-page.component.html',
  styleUrls: ['./faq-page.component.scss'],
})
export class FaqPageComponent implements OnInit {
  private faqsService = inject(FaqsService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  loading = true;
  error: string | null = null;
  allFaqs: PublicFaqItem[] = [];
  groups: FaqCategoryGroup[] = [];
  searchQuery = '';
  initialDisplayCount = 3;

  private apiGroups: PublicFaqGroup[] = [];
  private groupsLoaded = false;
  private scrollToCategory: string | null = null;

  private uncategorizedConfig = { icon: 'help-circle', color: '#64748b', bgColor: '#f8fafc' };

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const cat = params.get('category');
      if (cat) this.scrollToCategory = cat;
    });
    this.loadGroups();
    this.load();
  }

  private loadGroups(): void {
    this.faqsService.getPublicFaqGroups().subscribe({
      next: (groups) => {
        this.apiGroups = groups;
        this.groupsLoaded = true;
        if (this.allFaqs.length) this.buildGroups();
      },
      error: () => {
        this.groupsLoaded = true;
        if (this.allFaqs.length) this.buildGroups();
      }
    });
  }

  private load(): void {
    this.loading = true;
    this.error = null;
    this.faqsService.getPublicFaqs({ page: 1, perPage: 200 }).subscribe({
      next: (rows: PublicFaqItem[]) => {
        this.allFaqs = (rows || [])
          .filter((f) => f?.question && f?.answer)
          .map((f) => ({
            ...f,
            question: this.normalizeText(f.question),
            answer: this.normalizeText(f.answer),
          }))
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.loading = false;
        this.buildGroups();
      },
      error: (err) => {
        this.loading = false;
        this.allFaqs = [];
        this.error = err?.message || 'Failed to load FAQs';
      }
    });
  }

  private buildGroups(): void {
    const filtered = this.getFilteredFaqs();
    const map = new Map<string, PublicFaqItem[]>();

    for (const faq of filtered) {
      const cat = faq.category || 'Other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(faq);
    }

    this.groups = [];

    if (this.groupsLoaded && this.apiGroups.length) {
      for (const apiGroup of this.apiGroups) {
        const items = map.get(apiGroup.name);
        if (!items || !items.length) continue;
        map.delete(apiGroup.name);
        this.groups.push({
          category: apiGroup.name,
          slug: this.slugify(apiGroup.name),
          icon: apiGroup.icon,
          color: apiGroup.color,
          bgColor: apiGroup.bgColor,
          items,
          displayCount: items.length,
          expanded: true,
        });
      }
    }

    for (const [cat, items] of map) {
      this.groups.push({
        category: cat,
        slug: this.slugify(cat),
        icon: this.uncategorizedConfig.icon,
        color: this.uncategorizedConfig.color,
        bgColor: this.uncategorizedConfig.bgColor,
        items,
        displayCount: items.length,
        expanded: true,
      });
    }

    // Auto-scroll to category if specified in URL
    this.scrollToCategoryIfNeeded();
  }

  private getFilteredFaqs(): PublicFaqItem[] {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) return this.allFaqs;
    return this.allFaqs.filter(
      (f) => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }

  onSearchInput(): void {
    this.buildGroups();
  }

  toggleGroupExpanded(group: FaqCategoryGroup): void {
    group.expanded = !group.expanded;
    if (group.expanded) {
      group.displayCount = group.items.length;
    } else {
      group.displayCount = this.initialDisplayCount;
    }
  }

  viewAll(group: FaqCategoryGroup): void {
    group.expanded = true;
    group.displayCount = group.items.length;
  }

  getVisibleItems(group: FaqCategoryGroup): PublicFaqItem[] {
    return group.items.slice(0, group.displayCount);
  }

  hasMore(group: FaqCategoryGroup): boolean {
    return group.items.length > group.displayCount;
  }

  private scrollToCategoryIfNeeded(): void {
    if (!this.scrollToCategory || !this.groups.length) return;
    const target = this.scrollToCategory;
    this.scrollToCategory = null;
    setTimeout(() => {
      const el = document.getElementById(`faq-category-${target}`);
      if (el) {
        // Expand the group if it exists
        const group = this.groups.find((g) => g.slug === target);
        if (group && !group.expanded) {
          group.expanded = true;
          group.displayCount = group.items.length;
        }
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }

  shareCategory(group: FaqCategoryGroup): void {
    const url = `${window.location.origin}/faq?category=${encodeURIComponent(group.slug)}#${group.slug}`;
    navigator.clipboard.writeText(url).then(() => {
      group.shareCopied = true;
      setTimeout(() => { group.shareCopied = false; }, 2000);
    }).catch(() => {
      // Fallback: select a temporary input
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      group.shareCopied = true;
      setTimeout(() => { group.shareCopied = false; }, 2000);
    });
  }

  private slugify(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  // Rich-text pasted from Word/Quill often separates every word with a
  // non-breaking space. That suppresses normal word wrapping, so the browser
  // treats whole sentences as one unbreakable token and snaps them mid-character
  // ("deve/loper"). The stored HTML holds it as the `&nbsp;` *entity* (not the
  // U+00A0 char), so we normalize both forms back to regular spaces here as a
  // safety net for already-saved rows; new saves are cleaned in the admin editor.
  private normalizeText(value: string): string {
    return (value || '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\u00A0/g, ' ');
  }

  trackFaq(index: number, item: PublicFaqItem): string {
    return item.id || String(index);
  }
}